<template>
  <button
    :is="tagName"
    class="rr-button"
    :href="!disabled && href"
    :to="!disabled && to"
    :class="classes"
    :type="type"
    :disabled="disabled || disabledQuiet || loading"
    @click="$emit('click', $event)"
  >
    <slot name="before"></slot>
    <span
        class="rr-button__icon"
        :class="{'rr-button__icon_auto': iconSize}"
        v-if="iconLeft"
    >
      <Icon :name="iconLeft" :size="iconSize"></Icon>
    </span>
    <slot></slot>
    <span class="rr-button__text" v-if="text" v-html="text"></span>
    <span class="rr-button__icon rr-button__loading" v-if="loading">
      <Icon name="loading"></Icon>
    </span>
    <span class="rr-button__icon rr-button__done" v-if="done">
      <Icon name="check-bold"></Icon>
    </span>
    <span
        class="rr-button__icon"
        :class="{'rr-button__icon_auto': iconSize}"
        v-if="iconRight"
    >
      <Icon :name="iconRight" :size="iconSize"></Icon>
    </span>
    <slot name="after"></slot>
  </button>
</template>

<script>
import Icon from '@rr-component-library/icon/src/main';

export default {
  name: 'Button',
  designVersion: '2.1.1',
  components: {
    Icon,
  },
  props: {
    /**
     * Текст на кнопке
     */
    text: {
      type: String,
    },
    /**
     * Вариант кнопки
     */
    variant: {
      type: String,
      default: 'primary',
      validator(value) {
        return ['', 'primary', 'secondary', 'ghost', 'clear'].indexOf(value) !== -1;
      },
    },
    /**
     * Вид кнопки
     */
    view: {
      type: String,
      validator(value) {
        return ['', 'danger', 'success', 'light'].indexOf(value) !== -1;
      },
    },
    /**
     * Размер
     */
    size: {
      type: String,
      default: 'md',
      validator(value) {
        return ['', 'sm', 'md', 'lg'].indexOf(value) !== -1;
      },
    },
    /**
     * Форма
     */
    shape: {
      type: String,
      validator(value) {
        return ['square', 'circle', 'rounded'].indexOf(value) !== -1;
      },
    },
    /**
     * Кнопка на всю ширину контейнера
     */
    wide: {
      type: Boolean,
      default: false,
    },
    /**
     * HTML-атрибут href — адрес ссылки.<br>
     * Заменяет тег на \<a\>.
     */
    href: {
      type: String,
    },
    /**
     * Ссылка на маршрут vue-роутера.<br>
     * Заменяет тег на \<router-link\>.
     */
    to: {
      type: [String, Object],
    },
    /**
     * HTML-атрибут type — тип кнопки
     */
    type: {
      type: String,
      default: 'button',
      validator(value) {
        return ['button', 'submit', 'reset'].indexOf(value) !== -1;
      },
    },
    /**
     * HTML-тег
     */
    tag: {
      type: String,
    },
    /**
     * Блокировка кнопки
     */
    disabled: {
      type: Boolean,
      default: false,
    },
    /**
     * Блокировка кнопки без изменения внешнего вида
     */
    disabledQuiet: {
      type: Boolean,
      default: false,
    },
    /**
     * Отображает прелоадер вместо текста
     */
    loading: {
      type: Boolean,
      default: false,
    },
    /**
     * Отображает check иконку вместо текста
     */
    done: {
      type: Boolean,
      default: false,
    },
    /**
     * Иконка слева
     */
    iconLeft: {
      type: String,
    },
    /**
     * Иконка справа
     */
    iconRight: {
      type: String,
    },
    /**
     * Размер иконки
     */
    iconSize: {
      type: String,
      validator(value) {
        return ['', 'xxs', 'xs', 's', 'm', 'l', 'xl', 'xxl'].indexOf(value) !== -1;
      },
    },
    /**
     * Увеличенная кнопка на мобильных устройствах
     */
    mobile: {
      type: Boolean,
    },
  },
  computed: {
    tagName() {
      if (this.tag) {
        return this.tag;
      }
      if (this.href) {
        return 'a';
      }
      if (this.to) {
        return 'router-link';
      }
      return 'button';
    },
    classes() {
      const classes = {
        disabled: this.disabled,
        'rr-button_done': this.done,
        'rr-button_loading': this.loading,
        'rr-button_wide': this.wide,
        'rr-button_mobile': this.mobile,
      };
      [this.variant, this.view, this.size, this.shape].forEach((val) => {
        if (val) {
          classes[`rr-button_${val}`] = true;
        }
      });
      return classes;
    },
  },
};
</script>

<style lang="scss">
  @import "./Button";
</style>
